// Static entry-ladder content for the Strategy view.
// Source: sleeve ladders artifact (verified against code 2026-10-20, re-checked 2026-10-28), corrected for
// D078 (IPO hard-to-borrow check removed), D079 (dynamic range gate removed) and D080 (per-position cap).
// Thresholds that exist in StrategyConfig are read from it; the literals below are the fallbacks.
import type { StrategyConfig } from '../../lib/supabase'

export type SleeveKey = 'etf' | 'stock' | 'ipo' | 'commod' | 'crypto'
export type Cat = 'univ' | 'liq' | 'trend' | 'rs' | 'time' | 'house'
export type Kind = 'static' | 'dynamic' | 'review' | 'removed'

export interface Rung {
  cat: Cat
  title: string
  plain: string
  band: string
  kind: Kind
  chip?: string   // text for removed rungs, e.g. "removed, D079"
  anchor?: string
}

export interface SleeveContent {
  key: SleeveKey
  name: string
  fallbackTarget: number   // % of NAV, live targets (sum 100)
  bench: string
  meta: string
  rungs: Rung[]
  stop: string
  regime: { title: string; note: string }
}

export const CAT_LABEL: Record<Cat, string> = {
  univ: 'Universe', liq: 'Liquidity', trend: 'Trend', rs: 'Relative strength', time: 'Timing', house: 'Housekeeping',
}

// D080: per-position cap = min(NAV cap, 20% of the sleeve's regime-scaled target)
export const NAV_CAP_PCT: Record<SleeveKey, number> = { etf: 3, stock: 1.5, ipo: 1.5, commod: 3, crypto: 3 }
export const SLEEVE_SHARE_CAP = 0.2

const r = (cat: Cat, title: string, plain: string, band: string, kind: Kind = 'static', anchor?: string): Rung =>
  ({ cat, title, plain, band, kind, anchor })
const gone = (cat: Cat, title: string, plain: string, band: string, chip: string, anchor?: string): Rung =>
  ({ cat, title, plain, band, kind: 'removed', chip, anchor })

const MKT_ANCHOR = 'Anchor: lowest daily low of the last 72 sessions; if that low is under 12 sessions old, start 12 sessions back. VWAP of daily (O+H+L+C)/4 x volume from there.'
const D079 = 'removed, D079'
const rangeGone = (bm: string) => gone('time', 'Range expansion vs the market',
  `Removed from every sleeve. It compared one day's range with ${bm}, not with the name's own history. range_gate_* values are diagnostic only.`,
  'one-bar range gate (removed, D079)', D079)
const sold = () => r('house', 'Not just sold', 'No full exit in this sleeve in the last 2 days.', '2 NY calendar days')

function money(v: number): string {
  if (v >= 1e9) return `$${+(v / 1e9).toFixed(2)}B`
  if (v >= 1e6) return `$${+(v / 1e6).toFixed(0)}M`
  return `$${v}`
}
const vol = (v: number) => (v >= 1e6 ? `${+(v / 1e6).toFixed(2)}M` : `${Math.round(v / 1e3)}k`)

export function buildSleeves(cfg: StrategyConfig | null): SleeveContent[] {
  const f = cfg?.universe_filters
  const s = f?.stock, i = f?.ipo
  const cool = cfg?.p22k_gate_params?.cooldown_days ?? 2
  const rsTop = Math.round((cfg?.p22k_gate_params?.rs_gate_pct ?? 0.2) * 100)
  const mult = (k: string, d: number) => `${(cfg?.sleeve_multipliers?.[k] ?? d).toFixed(1)}`
  const soldTxt = sold()
  soldTxt.band = `${cool} NY calendar days`
  const sMc = money(s?.market_cap_min ?? 10e9)
  const sVol = vol(s?.avg_volume_5d_min ?? 750e3)
  const sDays = s?.days_since_ipo_min ?? 548
  const iMin = i?.days_since_ipo_min ?? 21, iMax = i?.days_since_ipo_max ?? 548

  return [
    { key: 'etf', name: 'ETF', fallbackTarget: 33.3, bench: 'RSP (equal-weight S&P)', meta: '20 fixed ETFs, lookback 12 days',
      stop: 'Highest of: VWAP from 20d low, 20d lowest low, hard stop.',
      regime: { title: 'Shared ETF + stock breadth: share of names scoring 65 or more', note: 'Above 1.00 only if SPY is above its 200d SMA. Stale data gives 0.75.' },
      rungs: [
        r('univ', 'The guest list', 'Only 20 hand-picked ETFs can ever be bought.', 'QQQ SMH XLK XLF XLE XLV XLI XLP XLY XLU XLB XLRE XLC EEM EFA VTI IJR VNQ SCHD AMLP'),
        r('liq', 'Normal price, plenty of trading', 'Skip anything too cheap, too pricey, or thinly traded.', '$5 < price < $1,500; 5d & 20d avg vol > 750k'),
        r('trend', 'Going up, not overheated', 'Up over the last 12 days, but not a spike.', '12d change > 0; 12d gain < 25%'),
        r('rs', 'Not lagging the market', 'Can trail RSP a little, not a lot. A floor, not "must beat".', '12d ROC - RSP 12d ROC > -5 pts', 'dynamic'),
        r('rs', 'Market is healthy', 'RSP itself must close above its anchored VWAP.', 'RSP close > RSP AVWAP', 'dynamic', MKT_ANCHOR),
        r('time', 'Holding its own floor', 'Yesterday it never dipped below the average price since its recent low.', 'low > own AVWAP (30-min bars)', 'dynamic', "Anchor: the ETF's own lowest daily low of the last 72 sessions, at least 12 sessions back."),
        r('time', 'Trend is waking up', 'Trend strength is accelerating.', 'ADX14 slope today > yesterday', 'dynamic'),
        rangeGone('RSP'),
        soldTxt] },
    { key: 'stock', name: 'Stock', fallbackTarget: 25, bench: 'Its own sector ETF (fallback RSP)', meta: 'jgl_symbols, re-qualified every Sunday, lookback 20 days, first CRS test sleeve',
      stop: 'Highest of: anchored VWAP, 20d lowest low, hard stop.',
      regime: { title: 'Shared ETF + stock breadth: share of names scoring 65 or more', note: 'Above 1.00 only if SPY is above its 200d SMA. Stale data gives 0.75.' },
      rungs: [
        r('univ', 'Big, established companies', 'Sunday scan: large, liquid, listed 18+ months, not wild or heavily shorted.',
          `mkt cap >= ${sMc}; price >= $${s?.price_floor ?? 5}; >= ${sDays} days listed; ATR ${s?.atr_pct_min ?? 1}-${s?.atr_pct_max ?? 8}%; vol >= ${sVol}; short int <= ${Math.round((s?.short_interest_max ?? 0.5) * 100)}%; gap <= ${s?.avg_gap_pct_max ?? 3}%`),
        r('trend', 'Sunday trend check', 'Already in an uptrend when the week starts.', 'close > SMA50; 60d ROC > 0; close > VWAP20; 20d ROC > sector', 'dynamic'),
        r('rs', `Top ${rsTop}% this week`, 'Only the strongest fifth of the pool by relative strength. Set Monday, frozen to Friday.', `top ${rsTop}% by RS margin (missing file = whole pool)`, 'dynamic'),
        r('liq', 'Normal price, plenty of trading', 'Same sanity check, done again on the morning.', '$5-$2,000; 5d & 20d vol > 750k'),
        r('trend', 'Going up, not overheated', 'Up over 20 days, but not a spike.', '20d change > 0; 20d gain < 25%'),
        r('rs', 'Not lagging its sector', 'Compared with its own sector ETF, not the whole market.', '20d ROC - sector ETF 20d ROC > -5 pts', 'dynamic'),
        r('rs', 'Its sector is healthy', 'The sector ETF (e.g. XLK for Apple) must close above its anchored VWAP.', 'sector ETF close > its AVWAP', 'dynamic', "Anchor: the sector ETF's lowest daily low of the last 72 sessions; uses the ETF window, not the stock sleeve's 150 / 20."),
        r('time', 'Holding its own floor', "Yesterday's low stayed above the stock's own anchored VWAP.", 'low > own AVWAP (30-min bars)', 'dynamic', "Anchor: the stock's own lowest daily low of the last 150 sessions, at least 20 sessions back."),
        r('time', 'Trend is waking up', 'Trend strength is accelerating.', 'ADX14 slope today > yesterday', 'dynamic'),
        rangeGone('RSP'),
        soldTxt] },
    { key: 'ipo', name: 'IPO', fallbackTarget: 16.7, bench: 'Renaissance IPO ETF (peer group)', meta: `${iMin}-${iMax} days since listing, then graduates to stock (D061)`,
      stop: 'Highest of: VWAP from IPO date, 20d lowest low, hard stop.',
      regime: { title: 'IPO ETF 20d ROC minus SPY 20d ROC', note: 'IPO ETF below its 50d SMA caps at 1.00. Stale gives 0.75.' },
      rungs: [
        r('univ', 'Recent listings only', 'Listed at least a month, under 18 months ago.', `${iMin}-${iMax} days since IPO`),
        r('univ', 'Real companies', 'Sunday scan: not tiny, not untradeably wild.',
          `price >= $${i?.price_floor ?? 10}; mkt cap >= ${money(i?.market_cap_min ?? 750e6)}; ATR ${i?.atr_pct_min ?? 1.5}-${i?.atr_pct_max ?? 12}%; 10d vol >= ${vol(i?.avg_volume_10d_min ?? 750e3)}; gap <= ${i?.avg_gap_pct_max ?? 4}%`,
          'static', 'ATR: 14-day average true range as a % of the last close, checked on Sunday. Under 1.5% is too quiet to trend; over 12% is too wild to hold.'),
        r('liq', 'Plenty of trading', 'Morning check is stricter than the scan.', '$10-$2,000; 5d & 20d vol > 1,000,000'),
        r('trend', 'Going up, not overheated', 'Up over 20 days, but not a spike.', '20d change > 0; 20d gain < 25%'),
        r('rs', 'Not lagging other IPOs', 'Measured against the IPO ETF, not the S&P.', '20d ROC - IPO ETF 20d ROC > -5 pts', 'dynamic'),
        r('rs', 'Market is healthy', 'Hard gate here (the scan only scores it).', 'RSP close > RSP AVWAP', 'dynamic', MKT_ANCHOR),
        r('time', 'Holding above its debut price', 'VWAP is anchored on the listing day.', 'low > AVWAP from IPO date', 'dynamic'),
        rangeGone('the IPO ETF'),
        gone('house', 'Hard-to-borrow check', 'It never ran: the sleeve is long only, so borrow does not matter, and the code called a broker method that does not exist.', 'HTB flag check', 'removed, D078'),
        gone('time', 'Trend accelerating', 'Removed for IPOs in June.', 'none (D070)', 'removed, D070'),
        soldTxt] },
    { key: 'commod', name: 'Commodity', fallbackTarget: 16.7, bench: 'Per group: GSG, GLD, GDX or CPER', meta: '13 fixed ETFs, own gate set (not the TrendQual gates)',
      stop: `Close - ${mult('commod', 2)} x ATR14.`,
      regime: { title: 'Share of the 13 above SMA50 with ROC60 > 0', note: 'Under 33% gives 0.50, 33-66% gives 1.00, 66% or more gives 1.50. Stale gives 0.75.' },
      rungs: [
        r('univ', 'The guest list', '13 commodity ETFs across six groups.', 'GLD SLV PALL GDX GDXJ SIL USO BNO DBA CPER COPX LIT REMX'),
        r('liq', 'Moves enough, trades enough', 'Needs real daily movement and volume.', 'ATR14 >= 2.0% of price; 5d & 20d vol >= 500k'),
        r('trend', 'Above its 50-day line', 'Basic uptrend.', 'close > SMA50', 'dynamic'),
        r('trend', '3-month momentum', 'Higher than three months ago.', 'ROC60 > 0', 'dynamic'),
        r('rs', 'Beats its own benchmark', 'Must actually outperform (strict), against the right comparison.', 'ROC20 > bench ROC20. GSG: GLD DBA USO BNO CPER LIT REMX; GLD: SLV PALL GDX SIL; GDX: GDXJ; CPER: COPX', 'dynamic'),
        r('time', 'Above its 20-day VWAP', 'Buyers in control recently.', 'close > VWAP20', 'dynamic'),
        rangeGone('GSG'),
        r('time', 'Trend is waking up', 'Trend strength accelerating.', 'ADX14 slope today > yesterday', 'dynamic'),
        r('house', 'No crowding', 'At most two per group (e.g. two miners).', 'max 2 held per group'),
        soldTxt] },
    { key: 'crypto', name: 'Crypto', fallbackTarget: 8.3, bench: 'The other coin (IBIT vs ETHE)', meta: '2 fixed ETFs, own gate set',
      stop: `Close - ${mult('crypto', 2)} x ATR14.`,
      regime: { title: 'How many of IBIT / ETHE are above SMA50 with ROC60 > 0', note: '0 of 2 gives 0.50, 1 of 2 gives 1.00, 2 of 2 gives 1.50. Stale gives 0.75.' },
      rungs: [
        r('univ', 'The guest list', 'Bitcoin and Ether ETFs only.', 'IBIT, ETHE'),
        r('liq', 'Moves enough, trades enough', 'Real movement and volume.', 'ATR14 >= 2.0%; 5d & 20d vol >= 500k'),
        r('trend', 'Above its 50-day line', 'Basic uptrend.', 'close > SMA50', 'dynamic'),
        r('trend', '3-month momentum', 'Higher than three months ago.', 'ROC60 > 0', 'dynamic'),
        r('rs', 'Stronger of the two', 'Must beat the other coin over 20 days.', 'ROC20 > other coin ROC20', 'dynamic'),
        r('time', 'Above its 20-day VWAP', 'Buyers in control recently.', 'close > VWAP20', 'dynamic'),
        rangeGone('IBIT'),
        r('time', 'Trend is waking up', 'Trend strength accelerating.', 'ADX14 slope today > yesterday', 'dynamic'),
        soldTxt] },
  ]
}
